import { createSlice } from '@reduxjs/toolkit';
import type { AppDispatch } from './store';
import type { SyncedTripLeg, TripLeg } from './models/reservation.model';
import type { PagedList } from './models/paged-list.model';
import type { SortModel } from './models/sort-model.model';
import type { Driver } from './models/driver.model';
import type { InventoryServiceConfiguration } from './models/inventory-service-configuration.model';
import {
  TripLegStatus,
  TripLegStatusBC,
  TripLegStatusToBC,
  TripStatusTodaysTripsBCList,
} from './models/trip-leg-status.model';
import { TripLegResponseItem } from './models/trip-leg.model';
import {
  TripLegRequestV2,
  TripLegSearchMenu,
  TripLegSearchRequest,
} from './requests/trip-legs';
import { TripLegDiscussionDecision } from './models/trip-leg-discussion-decision.model';
import { AssignTripLeg } from './models/trip-leg-assign.model';
import { RejectTripLegRequest } from './models/trip-leg-reject.model';
import {
  TripLegAllocationPropertiesRequest,
  TripLegAllocationPropertiesResponse,
} from './models/trip-leg-allocation.model';

import triplegService from '../services/triplegService';
import tripLegUtils from '../services/triplegUtils';

import * as uiSlice from './ui.slice';
import reservationService from '../services/reservationService';
import { TripLegEventType } from '../services/webSocketService/updateTripLeg';
import { TripLegSearchResponseItem } from './models/trip-leg-search.model';
import { isAbortedRequestError } from '@/services/httpService.utils';

type TripLegCounts = {
  today: number;
  all: number;
  discuss: number;
  escalated: number;
  date: string;
};

const mergeTripLegCounts = (
  current: TripLegCounts,
  updates?: Partial<TripLegCounts> | null,
): TripLegCounts => {
  const next = updates || {};
  return {
    today: next.today ?? current.today,
    all: next.all ?? current.all,
    discuss: next.discuss ?? current.discuss,
    escalated: next.escalated ?? current.escalated,
    date: next.date ?? current.date,
  };
};

interface TriplegState {
  tripLegsSearchResults: PagedList<TripLegSearchResponseItem>;
  loadingTripLegsSearch: boolean;
  loadedTripLegsSearch: boolean;

  tripLegInventory: TripLeg[];
  loadingTripLegInventory: boolean;
  loadedTripLegInventory: boolean;
  allTripLegs: PagedList<TripLegResponseItem>;
  loadingAllTripLegs: boolean;
  loadedAllTripLegs: boolean;

  todaysTripLegs: PagedList<TripLeg>;
  loadingTodaysTripLegs: boolean;
  loadedTodaysTripLegs: boolean;

  allTripLegsInReservation: TripLeg[];
  loadingAllTripLegsInReservation: boolean;
  loadedAllTripLegsInReservation: boolean;

  inventoryPage: number;
  inventoryPageSize: number;
  inventoryPageSortModel: SortModel | null;
  monitorPage: number;
  monitorPageSize: number;
  monitorPageSortModel: SortModel | null;
  discussionPage: number;
  discussionPageSize: number;
  discussionPageSortModel: SortModel | null;
  discussionTripLegs: PagedList<TripLegSearchResponseItem>;
  loadingDiscussionTripLegs: boolean;
  loadedDiscussionTripLegs: boolean;
  invoicePage: number;
  invoicePageSize: number;
  invoicePageSortModel: SortModel | null;
  invoiceTripLegs: PagedList<TripLegResponseItem>;
  loadingInvoiceTripLegs: boolean;
  loadedInvoiceTripLegs: boolean;
  todayPage: number;
  todayPageSize: number;
  todayPageSortModel: SortModel | null;
  savingTripLeg: boolean;
  savedTripLeg: boolean;

  tripLegDetails: { [id: string]: SyncedTripLeg };
  loadingTripLegDetails: boolean;

  providersMatchingTripLeg: {
    transportationProviderIDs: number[];
  };
  loadingProvidersMatchingTripLeg: boolean;
  loadedProvidersMatchingTripLeg: boolean;

  serviceConfiguration: InventoryServiceConfiguration | null;
  loadingServiceConfiguration: boolean;
  loadedServiceConfiguration: boolean;
  savingServiceConfiguration: boolean;
  savedServiceConfiguration: boolean;
  todaysTripsWithExceptions: PagedList<TripLeg>;

  escalatedTripLeg: boolean;
  tripLegEvent: { detailType: TripLegEventType; detail: TripLeg } | null;

  tripLegDiscussion: TripLegRequestV2 | null;
  loadingTripLegDiscussion: boolean;
  loadedTripLegDiscussion: boolean;
  clearingDiscussion: boolean;
  clearedDiscussion: boolean;

  validTripLegAllocationProperties: TripLegAllocationPropertiesResponse | null;
  loadingValidTripLegAllocationProperties: boolean;
  loadedValidTripLegAllocationProperties: boolean;

  reservationTripLegs: {
    reservationReferenceID: string;
    tripLegs: TripLegResponseItem[];
  } | null;
  loadingReservationTripLegs: boolean;
  loadedReservationTripLegs: boolean;
  tripLegCounts: TripLegCounts;
  loadingTripLegCounts: boolean;
  loadedTripLegCounts: boolean;

  monitoringExceptionsTripLegs: PagedList<TripLeg>;
  loadingMonitoringExceptionsTripLegs: boolean;
  loadedMonitoringExceptionsTripLegs: boolean;

  savingTripLegException: boolean;
  savedTripLegException: boolean;

  pendingDiscussionClear: {
    tripLegID: number;
    tripLegReferenceID: string;
    decision: TripLegDiscussionDecision;
    tripLegExceptionID?: number;
  } | null;
}

export const initialState: TriplegState = {
  tripLegsSearchResults: {
    currentPage: 1,
    totalPages: 0,
    itemCount: 0,
    items: [],
  },
  loadingTripLegsSearch: false,
  loadedTripLegsSearch: false,

  tripLegInventory: [],
  loadingTripLegInventory: false,
  loadedTripLegInventory: false,
  allTripLegs: { currentPage: 1, totalPages: 0, itemCount: 0, items: [] },
  loadingAllTripLegs: false,
  loadedAllTripLegs: false,

  todaysTripLegs: { currentPage: 1, totalPages: 0, itemCount: 0, items: [] },
  loadingTodaysTripLegs: false,
  loadedTodaysTripLegs: false,

  allTripLegsInReservation: [],
  loadingAllTripLegsInReservation: false,
  loadedAllTripLegsInReservation: false,

  inventoryPage: 1,
  inventoryPageSize: 20,
  inventoryPageSortModel: {
    column: 'appointmentDate,pickUpDueTimeUTC',
    direction: 'asc,asc',
  },
  monitorPage: 1,
  monitorPageSize: 10,
  monitorPageSortModel: {
    column: 'appointmentDate,pickUpDueTimeUTC',
    direction: 'asc,asc',
  },
  discussionPage: 1,
  discussionPageSize: 20,
  discussionPageSortModel: {
    column: 'pickUpDueTimeUTC',
    direction: 'asc',
  },
  discussionTripLegs: {
    currentPage: 1,
    totalPages: 0,
    itemCount: 0,
    items: [],
  },
  loadingDiscussionTripLegs: false,
  loadedDiscussionTripLegs: false,
  invoicePage: 1,
  invoicePageSize: 20,
  invoicePageSortModel: {
    column: 'pickUpDueTimeUTC',
    direction: 'asc',
  },
  invoiceTripLegs: {
    currentPage: 1,
    totalPages: 0,
    itemCount: 0,
    items: [],
  },
  loadingInvoiceTripLegs: false,
  loadedInvoiceTripLegs: false,
  todayPage: 1,
  todayPageSize: 10,
  todayPageSortModel: {
    column: 'appointmentDate,pickUpDueTimeUTC',
    direction: 'asc,asc',
  },
  savingTripLeg: false,
  savedTripLeg: false,

  tripLegDetails: {},
  loadingTripLegDetails: false,

  providersMatchingTripLeg: {
    transportationProviderIDs: [],
  },
  loadingProvidersMatchingTripLeg: false,
  loadedProvidersMatchingTripLeg: false,

  serviceConfiguration: null,
  loadingServiceConfiguration: false,
  loadedServiceConfiguration: false,
  savingServiceConfiguration: false,
  savedServiceConfiguration: false,
  todaysTripsWithExceptions: {
    currentPage: 1,
    totalPages: 0,
    itemCount: 0,
    items: [],
  },

  escalatedTripLeg: false,
  tripLegEvent: null,

  tripLegDiscussion: null,
  loadingTripLegDiscussion: false,
  loadedTripLegDiscussion: false,
  clearingDiscussion: false,
  clearedDiscussion: false,

  validTripLegAllocationProperties: null,
  loadingValidTripLegAllocationProperties: false,
  loadedValidTripLegAllocationProperties: false,

  reservationTripLegs: null,
  loadingReservationTripLegs: false,
  loadedReservationTripLegs: false,

  tripLegCounts: {
    today: 0,
    all: 0,
    discuss: 0,
    escalated: 0,
    date: new Date().toISOString(),
  },
  loadingTripLegCounts: false,
  loadedTripLegCounts: false,

  monitoringExceptionsTripLegs: {
    currentPage: 1,
    totalPages: 0,
    itemCount: 0,
    items: [],
  },
  loadingMonitoringExceptionsTripLegs: false,
  loadedMonitoringExceptionsTripLegs: false,

  savingTripLegException: false,
  savedTripLegException: false,

  pendingDiscussionClear: null,
};

export const slice = createSlice({
  name: 'tripLegMonitoring',
  initialState,
  reducers: {
    setLoadingTripLegsSearch: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegsSearch = action.payload;
    },
    setLoadedTripLegsSearch: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLegsSearch = action.payload;
    },
    setTripLegsSearchResults: (
      state: TriplegState,
      action: { payload: PagedList<TripLegSearchResponseItem> },
    ) => {
      state.loadingTripLegsSearch = false;
      state.loadedTripLegsSearch = true;
      state.tripLegsSearchResults = action.payload;
    },
    setLoadingTripLegInventory: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegInventory = action.payload;
    },
    setLoadedTripLegInventory: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLegInventory = action.payload;
    },
    setTripLegInventory: (
      state: TriplegState,
      action: { payload: TripLeg[] },
    ) => {
      state.loadingTripLegInventory = false;
      state.loadedTripLegInventory = true;
      state.tripLegInventory = action.payload;
    },
    setLoadingAllTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingAllTripLegs = action.payload;
    },
    setLoadedAllTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedAllTripLegs = action.payload;
    },
    setAllTripLegs: (
      state: TriplegState,
      action: { payload: PagedList<TripLegResponseItem> },
    ) => {
      state.allTripLegs = action.payload;
    },
    setAllTripLegsInReservation: (
      state: TriplegState,
      action: { payload: TripLeg[] },
    ) => {
      state.allTripLegsInReservation = action.payload;
    },
    clearAllTripLegsInReservation: (state: TriplegState) => {
      state.allTripLegsInReservation = [];
      state.loadedAllTripLegsInReservation = false;
    },
    setLoadingAllTripLegsInReservation: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingAllTripLegsInReservation = action.payload;
    },
    setLoadedAllTripLegsInReservation: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedAllTripLegsInReservation = action.payload;
    },
    setLoadingTodaysTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTodaysTripLegs = action.payload;
    },
    setLoadedTodaysTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTodaysTripLegs = action.payload;
    },
    setTodaysTripLegs: (
      state: TriplegState,
      action: { payload: PagedList<TripLeg> },
    ) => {
      state.todaysTripLegs = action.payload;
    },
    setTodaysTripsWithExceptions: (
      state: TriplegState,
      action: { payload: PagedList<TripLeg> },
    ) => {
      state.todaysTripsWithExceptions = action.payload;
    },
    setSavingTripLeg: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTripLeg = action.payload;
    },
    setSavedTripLeg: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTripLeg = action.payload;
    },
    setInventoryPage: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.inventoryPage = action.payload;
    },
    setInventoryPageSize: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.inventoryPageSize = action.payload;
    },
    setInventoryPageSortModel: (
      state: TriplegState,
      action: { type: string; payload: SortModel | null },
    ) => {
      state.inventoryPageSortModel = action.payload;
    },
    setMonitorPage: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.monitorPage = action.payload;
    },
    setMonitorPageSize: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.monitorPageSize = action.payload;
    },
    setMonitorPageSortModel: (
      state: TriplegState,
      action: { type: string; payload: SortModel | null },
    ) => {
      state.monitorPageSortModel = action.payload;
    },
    setDiscussionPage: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.discussionPage = action.payload;
    },
    setDiscussionPageSize: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.discussionPageSize = action.payload;
    },
    setDiscussionPageSortModel: (
      state: TriplegState,
      action: { type: string; payload: SortModel | null },
    ) => {
      state.discussionPageSortModel = action.payload;
    },
    setLoadingDiscussionTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingDiscussionTripLegs = action.payload;
    },
    setLoadedDiscussionTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedDiscussionTripLegs = action.payload;
    },
    setDiscussionTripLegs: (
      state: TriplegState,
      action: { payload: PagedList<TripLegSearchResponseItem> },
    ) => {
      state.discussionTripLegs = action.payload;
    },
    removeDiscussionTripLeg: (
      state: TriplegState,
      action: { payload: number },
    ) => {
      const tripLegs = [...state.discussionTripLegs.items];
      const idx = tripLegs.findIndex((tl) => tl.tripLegID === action.payload);
      if (idx >= 0) {
        tripLegs.splice(idx, 1);
        state.discussionTripLegs = {
          ...state.discussionTripLegs,
          items: tripLegs,
          itemCount: tripLegs.length,
        };
      }
    },
    setInvoicePage: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.invoicePage = action.payload;
    },
    setInvoicePageSize: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.invoicePageSize = action.payload;
    },
    setInvoicePageSortModel: (
      state: TriplegState,
      action: { type: string; payload: SortModel | null },
    ) => {
      state.invoicePageSortModel = action.payload;
    },
    setLoadingInvoiceTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingInvoiceTripLegs = action.payload;
    },
    setLoadedInvoiceTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedInvoiceTripLegs = action.payload;
    },
    setInvoiceTripLegs: (
      state: TriplegState,
      action: { payload: PagedList<TripLegResponseItem> },
    ) => {
      state.invoiceTripLegs = action.payload;
    },
    setTodayPage: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.todayPage = action.payload;
    },
    setTodayPageSize: (
      state: TriplegState,
      action: { type: string; payload: number },
    ) => {
      state.todayPageSize = action.payload;
    },
    setTodayPageSortModel: (
      state: TriplegState,
      action: { type: string; payload: SortModel | null },
    ) => {
      state.todayPageSortModel = action.payload;
    },
    removeTripLeg: (
      state: TriplegState,
      action: { type: string; payload: TripLeg },
    ) => {
      const legs = [...state.tripLegInventory];
      const idx = legs.findIndex(
        (l) =>
          l.tripLegReferenceID === action.payload.tripLegReferenceID &&
          l.clientBC === action.payload.clientBC,
      );
      if (idx >= 0) {
        legs.splice(idx, 1);
        state.tripLegInventory = legs;
      }
    },
    addTripLegDetail: (
      state: TriplegState,
      action: { type: string; payload: SyncedTripLeg }, // TODO: this needs to change
    ) => {
      state.tripLegDetails[action.payload.tripLegReferenceID!] = action.payload;
    },
    setLoadingTripLegDetails: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegDetails = action.payload;
    },
    clearTripLegDetails: (
      state: TriplegState,
      action: { type: string }, // TODO: this needs to change
    ) => {
      state.tripLegDetails = {};
    },
    upsertTripLeg: (
      state: TriplegState,
      action: { type: string; payload: TripLeg },
    ) => {
      const newInventory = state.tripLegInventory.map((tripLeg) => {
        if (tripLeg.tripLegReferenceID === action.payload.tripLegReferenceID) {
          return action.payload;
        } else {
          return tripLeg;
        }
      });
      if (!newInventory.length) {
        state.tripLegInventory = [action.payload];
      } else {
        state.tripLegInventory = newInventory;
      }
    },
    upsertTodaysTripLegsFromEvent: (
      state: TriplegState,
      action: {
        type: string;
        payload: TripLeg;
      },
    ) => {
      const items = [...state.todaysTripLegs.items];
      const foundIndex = items.findIndex(
        (tripLeg) =>
          tripLeg.tripLegReferenceID === action.payload.tripLegReferenceID,
      );
      // if exists, and payload status is in TripStatusTodaysTripsBCList update the trip leg
      // if exists, but payload status is not in TripStatusTodaysTripsBCList, then remove
      // if does not exist, and payload status is in TripStatusTodaysTripsBCList insert the trip leg
      if (foundIndex >= 0) {
        if (
          action.payload.status &&
          TripStatusTodaysTripsBCList.includes(action.payload.status)
        ) {
          const newTodaysTripLegsItems = items.map((tripLeg) => {
            if (
              tripLeg.tripLegReferenceID === action.payload.tripLegReferenceID
            ) {
              const newTripLeg = tripLegUtils.upsertTripLegData(
                tripLeg,
                action.payload,
              );
              return newTripLeg;
            } else {
              return tripLeg;
            }
          });
          const newTodaysTripLegs = {
            ...state.todaysTripLegs,
            items: [...newTodaysTripLegsItems],
          };
          state.todaysTripLegs = newTodaysTripLegs;
        } else {
          const newTodaysTripLegsItems = items.filter(
            (tripLeg) =>
              tripLeg.tripLegReferenceID !== action.payload.tripLegReferenceID,
          );
          const newTodaysTripLegs = {
            ...state.todaysTripLegs,
            items: [...newTodaysTripLegsItems],
          };
          state.todaysTripLegs = newTodaysTripLegs;
        }
      } else {
        // TODO: currently we cannot insert directly from the socket event, we need to fetch the data from the API
      }
    },
    setEscalatedTripLeg: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.escalatedTripLeg = action.payload;
    },
    handleTripLegEscalated: (
      state: TriplegState,
      action: { type: string; payload: string },
    ) => {
      const allItems = [...state.allTripLegs.items];
      const foundIndex = allItems.findIndex(
        (tripLeg) => tripLeg.tripLegReferenceID === action.payload,
      );
      if (foundIndex >= 0) {
        allItems[foundIndex].isEscalated = true;
      }
      state.allTripLegs = { ...state.allTripLegs, items: allItems };
      const todaysItems = [...state.todaysTripLegs.items];
      const todaysFoundIndex = todaysItems.findIndex(
        (tripLeg) => tripLeg.tripLegReferenceID === action.payload,
      );
      if (todaysFoundIndex >= 0) {
        todaysItems[todaysFoundIndex].isEscalated = true;
      }
      state.todaysTripLegs = { ...state.todaysTripLegs, items: todaysItems };
      state.escalatedTripLeg = true;
    },
    reopenRejectedTripLeg: (
      state: TriplegState,
      action: { type: string; payload: string },
    ) => {
      const allItems = [...state.allTripLegs.items];
      const foundIndex = allItems.findIndex(
        (tripLeg) => tripLeg.tripLegReferenceID === action.payload,
      );
      if (foundIndex >= 0) {
        allItems[foundIndex].tripLegRejectedReasons = '';
      }
      state.allTripLegs = { ...state.allTripLegs, items: allItems };
    },
    upsertAllTripLegsFromEvent: (
      state: TriplegState,
      action: {
        type: string;
        payload: TripLeg;
      },
    ) => {
      const items = [...state.allTripLegs.items];
      const newAllTripLegsItems = items.map((tripLeg) => {
        if (tripLeg.tripLegReferenceID === action.payload.tripLegReferenceID) {
          const newTripLeg = tripLegUtils.upsertTripLegData(
            tripLeg as unknown as TripLeg,
            action.payload,
          );
          return newTripLeg;
        } else {
          return tripLeg;
        }
      });
      const newAllTripLegs = {
        ...state.allTripLegs,
        items: [...newAllTripLegsItems],
      };
      state.allTripLegs = newAllTripLegs as PagedList<TripLegResponseItem>;
    },
    upsertAllTripLegsDriverAssigned: (
      state: TriplegState,
      action: {
        type: string;
        payload: {
          tripLegReferenceID: string;
          transportationProviderID: number;
          driver: Driver;
        };
      },
    ) => {
      const items = [...state.allTripLegs.items];
      const newAllTripLegsItems = items.map((tripLeg) => {
        if (tripLeg.tripLegReferenceID === action.payload.tripLegReferenceID) {
          const newTripLeg = { ...tripLeg } as unknown as TripLeg;
          newTripLeg.driver = action.payload.driver;
          newTripLeg.transportationProviderID =
            action.payload.transportationProviderID;
          return newTripLeg;
        } else {
          return tripLeg;
        }
      });
      state.allTripLegs = {
        ...state.allTripLegs,
        items: newAllTripLegsItems as unknown as TripLegResponseItem[],
      };
    },
    setTripLegEventEmitted: (
      state: TriplegState,
      action: {
        type: string;
        payload: { detailType: TripLegEventType; detail: TripLeg } | null;
      },
    ) => {
      state.tripLegEvent = action.payload;
    },
    setServiceConfiguration: (
      state: TriplegState,
      action: { type: string; payload: any },
    ) => {
      state.serviceConfiguration = action.payload;
    },
    setLoadingServiceConfiguration: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingServiceConfiguration = action.payload;
    },
    setLoadedServiceConfiguration: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedServiceConfiguration = action.payload;
    },
    setSavingServiceConfiguration: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingServiceConfiguration = action.payload;
    },
    setSavedServiceConfiguration: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedServiceConfiguration = action.payload;
    },
    setProvidersMatchingTripLeg: (
      state: TriplegState,
      action: {
        type: string;
        payload: { transportationProviderIDs: number[] };
      },
    ) => {
      state.providersMatchingTripLeg = action.payload;
    },
    setLoadingProvidersMatchingTripLeg: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProvidersMatchingTripLeg = action.payload;
    },
    setLoadedProvidersMatchingTripLeg: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProvidersMatchingTripLeg = action.payload;
    },
    setLoadingTripLegDiscussion: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegDiscussion = action.payload;
    },
    setLoadedTripLegDiscussion: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLegDiscussion = action.payload;
    },
    setTripLegDiscussion: (
      state: TriplegState,
      action: { type: string; payload: TripLegRequestV2 | null },
    ) => {
      state.tripLegDiscussion = action.payload;
    },
    setClearingDiscussion: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.clearingDiscussion = action.payload;
    },
    setClearedDiscussion: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.clearedDiscussion = action.payload;
    },
    setValidTripLegAllocationProperties: (
      state: TriplegState,
      action: {
        type: string;
        payload: TripLegAllocationPropertiesResponse | null;
      },
    ) => {
      state.validTripLegAllocationProperties = action.payload;
    },
    setLoadingValidTripLegAllocationProperties: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingValidTripLegAllocationProperties = action.payload;
    },
    setLoadedValidTripLegAllocationProperties: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedValidTripLegAllocationProperties = action.payload;
    },
    setReservationTripLegs: (
      state: TriplegState,
      action: {
        payload: {
          reservationReferenceID: string;
          tripLegs: PagedList<TripLegResponseItem>;
        } | null;
      },
    ) => {
      if (action.payload === null) {
        state.reservationTripLegs = null;
      } else {
        state.reservationTripLegs = {
          reservationReferenceID: action.payload.reservationReferenceID,
          tripLegs: action.payload.tripLegs.items,
        };
      }
    },
    setLoadingReservationTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingReservationTripLegs = action.payload;
    },
    setLoadedReservationTripLegs: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedReservationTripLegs = action.payload;
    },
    setTripLegCounts: (
      state: TriplegState,
      action: { payload: Partial<TripLegCounts> | null },
    ) => {
      state.tripLegCounts = mergeTripLegCounts(
        state.tripLegCounts,
        action.payload,
      );
    },
    setLoadingTripLegCounts: (
      state: TriplegState,
      action: { payload: boolean },
    ) => {
      state.loadingTripLegCounts = action.payload;
    },
    setLoadedTripLegCounts: (
      state: TriplegState,
      action: { payload: boolean },
    ) => {
      state.loadedTripLegCounts = action.payload;
    },
    setMonitoringExceptionsTripLegs: (
      state: TriplegState,
      action: { payload: PagedList<TripLeg> },
    ) => {
      state.monitoringExceptionsTripLegs = action.payload;
    },
    setLoadingMonitoringExceptionsTripLegs: (
      state: TriplegState,
      action: { payload: boolean },
    ) => {
      state.loadingMonitoringExceptionsTripLegs = action.payload;
    },
    setLoadedMonitoringExceptionsTripLegs: (
      state: TriplegState,
      action: { payload: boolean },
    ) => {
      state.loadedMonitoringExceptionsTripLegs = action.payload;
    },
    setSavingTripLegException: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTripLegException = action.payload;
    },
    setSavedTripLegException: (
      state: TriplegState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTripLegException = action.payload;
    },
    setPendingDiscussionClear: (
      state: TriplegState,
      action: {
        type: string;
        payload: {
          tripLegID: number;
          tripLegReferenceID: string;
          decision: TripLegDiscussionDecision;
          tripLegExceptionID?: number;
        } | null;
      },
    ) => {
      state.pendingDiscussionClear = action.payload;
    },
  },
});

export const { setLoadingTripLegsSearch } = slice.actions;
export const { setLoadedTripLegsSearch } = slice.actions;
export const { setTripLegsSearchResults } = slice.actions;
export const { setLoadingTripLegInventory } = slice.actions;
export const { setLoadedTripLegInventory } = slice.actions;
export const { setTripLegInventory } = slice.actions;
export const { setLoadingAllTripLegs } = slice.actions;
export const { setLoadedAllTripLegs } = slice.actions;
export const { setAllTripLegs } = slice.actions;
export const { setAllTripLegsInReservation } = slice.actions;
export const { clearAllTripLegsInReservation } = slice.actions;
export const { setLoadingAllTripLegsInReservation } = slice.actions;
export const { setLoadedAllTripLegsInReservation } = slice.actions;
export const { setLoadingTodaysTripLegs } = slice.actions;
export const { setLoadedTodaysTripLegs } = slice.actions;
export const { setTodaysTripLegs } = slice.actions;
export const { setSavingTripLeg } = slice.actions;
export const { setSavedTripLeg } = slice.actions;
export const { setInventoryPage } = slice.actions;
export const { setInventoryPageSize } = slice.actions;
export const { setInventoryPageSortModel } = slice.actions;
export const { setMonitorPage } = slice.actions;
export const { setMonitorPageSize } = slice.actions;
export const { setMonitorPageSortModel } = slice.actions;
export const { setDiscussionPage } = slice.actions;
export const { setDiscussionPageSize } = slice.actions;
export const { setDiscussionPageSortModel } = slice.actions;
export const { setLoadingDiscussionTripLegs } = slice.actions;
export const { setLoadedDiscussionTripLegs } = slice.actions;
export const { setDiscussionTripLegs } = slice.actions;
export const { removeDiscussionTripLeg } = slice.actions;
export const { setInvoicePage } = slice.actions;
export const { setInvoicePageSize } = slice.actions;
export const { setInvoicePageSortModel } = slice.actions;
export const { setLoadingInvoiceTripLegs } = slice.actions;
export const { setLoadedInvoiceTripLegs } = slice.actions;
export const { setInvoiceTripLegs } = slice.actions;
export const { setTodayPage } = slice.actions;
export const { setTodayPageSize } = slice.actions;
export const { setTodayPageSortModel } = slice.actions;
export const { removeTripLeg } = slice.actions;
export const { addTripLegDetail } = slice.actions;
export const { setLoadingTripLegDetails } = slice.actions;
export const { clearTripLegDetails } = slice.actions;
export const { upsertTripLeg } = slice.actions;
export const { upsertTodaysTripLegsFromEvent } = slice.actions;
export const { upsertAllTripLegsFromEvent } = slice.actions;
export const { upsertAllTripLegsDriverAssigned } = slice.actions;
export const { setTripLegEventEmitted } = slice.actions;
export const { setServiceConfiguration } = slice.actions;
export const { setLoadingServiceConfiguration } = slice.actions;
export const { setLoadedServiceConfiguration } = slice.actions;
export const { setSavingServiceConfiguration } = slice.actions;
export const { setSavedServiceConfiguration } = slice.actions;
export const { setTodaysTripsWithExceptions } = slice.actions;
export const { setEscalatedTripLeg } = slice.actions;
export const { handleTripLegEscalated } = slice.actions;
export const { reopenRejectedTripLeg } = slice.actions;
export const { setProvidersMatchingTripLeg } = slice.actions;
export const { setLoadingProvidersMatchingTripLeg } = slice.actions;
export const { setLoadedProvidersMatchingTripLeg } = slice.actions;
export const { setTripLegDiscussion } = slice.actions;
export const { setLoadingTripLegDiscussion } = slice.actions;
export const { setLoadedTripLegDiscussion } = slice.actions;
export const { setClearingDiscussion } = slice.actions;
export const { setClearedDiscussion } = slice.actions;
export const { setValidTripLegAllocationProperties } = slice.actions;
export const { setLoadingValidTripLegAllocationProperties } = slice.actions;
export const { setLoadedValidTripLegAllocationProperties } = slice.actions;
export const { setReservationTripLegs } = slice.actions;
export const { setLoadingReservationTripLegs } = slice.actions;
export const { setLoadedReservationTripLegs } = slice.actions;
export const { setTripLegCounts } = slice.actions;
export const { setLoadingTripLegCounts } = slice.actions;
export const { setLoadedTripLegCounts } = slice.actions;
export const { setMonitoringExceptionsTripLegs } = slice.actions;
export const { setLoadingMonitoringExceptionsTripLegs } = slice.actions;
export const { setLoadedMonitoringExceptionsTripLegs } = slice.actions;
export const { setSavingTripLegException } = slice.actions;
export const { setSavedTripLegException } = slice.actions;
export const { setPendingDiscussionClear } = slice.actions;

export const fetchTripLegInventoryAsync =
  (unallocatedOnly: boolean) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegInventory(true));
    dispatch(setLoadedTripLegInventory(false));
    triplegService.getTripLegInventory(unallocatedOnly).subscribe((legs) => {
      dispatch(setTripLegInventory(legs.tripLegs));
      dispatch(setLoadingTripLegInventory(false));
      dispatch(setLoadedTripLegInventory(true));
    });
  };

export const fetchTripLegCountsAsync = () => (dispatch: AppDispatch) => {
  dispatch(setLoadingTripLegCounts(true));
  dispatch(setLoadedTripLegCounts(false));
  triplegService.getTripLegCounts().subscribe({
    next: (counts) => {
      dispatch(setTripLegCounts(counts));
      dispatch(setLoadingTripLegCounts(false));
      dispatch(setLoadedTripLegCounts(true));
    },
    error: (err) => {
      dispatch(setLoadingTripLegCounts(false));
      dispatch(uiSlice.setErrorMessage(err.message));
    },
  });
};

export const fetchTripLegsSearchAsync =
  (request: TripLegSearchRequest & { signal?: AbortSignal }) =>
  (dispatch: AppDispatch) => {
    const { signal, ...requestWithoutSignal } = request;
    dispatch(setLoadingTripLegsSearch(true));
    dispatch(setLoadedTripLegsSearch(false));
    triplegService.getTripLegsSearch(requestWithoutSignal, signal).subscribe({
      next: (response) => {
        if (signal?.aborted) {
          return;
        }
        dispatch(setTripLegsSearchResults(response));
        dispatch(setLoadingTripLegsSearch(false));
        dispatch(setLoadedTripLegsSearch(true));
      },
      error: (err) => {
        if (isAbortedRequestError(err) || signal?.aborted) {
          return;
        }
        dispatch(setLoadingTripLegsSearch(false));
        dispatch(setLoadedTripLegsSearch(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const fetchTodaysTripLegsAsync =
  ({
    page,
    pageSize,
    sortModel,
    tripLegStatusBCs,
    pickupStartDate,
    pickupEndDate,
    providerAssigned,
    includeTripLegStatusLog,
    includeFindProviderCallOutcomes,
    searchText,
    isEscalated,
  }: {
    page: number;
    pageSize: number;
    sortModel: SortModel | null;
    tripLegStatusBCs?: string[];
    pickupStartDate?: string;
    pickupEndDate?: string;
    providerAssigned?: boolean;
    includeTripLegStatusLog?: boolean;
    includeFindProviderCallOutcomes?: boolean;
    searchText?: string;
    isEscalated?: boolean;
  }) =>
  (dispatch: AppDispatch, getState: () => any) => {
    dispatch(setLoadingTodaysTripLegs(true));
    dispatch(setLoadedTodaysTripLegs(false));
    const state = getState();
    const userInfo = state?.user?.loggedInUserInfo;
    const userCategoryTypeBC = userInfo?.userCategoryTypeBC;
    const clientBC = userInfo?.ids?.clientBC as string | undefined;
    triplegService
      .getTripLegsV2({
        page,
        pageSize,
        sortModel: sortModel || undefined,
        tripLegStatusBCs,
        pickupStartDate,
        pickupEndDate,
        providerAssigned,
        search: searchText,
        isEscalated,
        clientBC: userCategoryTypeBC === 'CLIENT' ? clientBC : undefined,
      })
      .subscribe({
        next: (response) => {
          dispatch(
            setTodaysTripLegs(response as unknown as PagedList<TripLeg>),
          );
          dispatch(setLoadingTodaysTripLegs(false));
          dispatch(setLoadedTodaysTripLegs(true));
        },
        error: (err) => {
          dispatch(setLoadingTodaysTripLegs(false));
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const fetchDiscussionTripLegsAsync =
  ({ page, pageSize, search, menu, sort, sortOrder }: TripLegSearchRequest) =>
  (dispatch: AppDispatch, getState: () => any) => {
    dispatch(setLoadingDiscussionTripLegs(true));
    dispatch(setLoadedDiscussionTripLegs(false));
    dispatch(
      setDiscussionTripLegs({
        currentPage: 1,
        totalPages: 0,
        itemCount: 0,
        items: [],
      }),
    );
    const state = getState();
    const userInfo = state?.user?.loggedInUserInfo;
    const userCategoryTypeBC = userInfo?.userCategoryTypeBC;
    const clientBC = userInfo?.ids?.clientBC as string | undefined;
    triplegService
      .getTripLegsSearch({
        page,
        pageSize,
        sort: sort || undefined,
        sortOrder: sortOrder || undefined,
        menu: TripLegSearchMenu.DISCUSS,
        search,
        clientBC: userCategoryTypeBC === 'CLIENT' ? clientBC : undefined,
      })
      .subscribe({
        next: (legs) => {
          dispatch(setDiscussionTripLegs(legs));
          dispatch(setLoadingDiscussionTripLegs(false));
          dispatch(setLoadedDiscussionTripLegs(true));
        },
        error: (err) => {
          dispatch(setLoadingDiscussionTripLegs(false));
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const allocateTripLegToProviderAsync =
  (tripLegInfo: AssignTripLeg) => (dispatch: AppDispatch) => {
    dispatch(setSavingTripLeg(true));
    dispatch(setSavedTripLeg(false));
    triplegService.assignTripLegToProvider(tripLegInfo).subscribe({
      next: (tripLeg) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.tripLegAssignedSuccess'));
        dispatch(removeTripLeg(tripLeg));
        dispatch(setSavingTripLeg(false));
        dispatch(setSavedTripLeg(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingTripLeg(false));
      },
    });
  };

export const fetchValidTripLegAllocationProperties =
  (request: TripLegAllocationPropertiesRequest) => (dispatch: AppDispatch) => {
    dispatch(setLoadingValidTripLegAllocationProperties(true));
    dispatch(setLoadedValidTripLegAllocationProperties(false));
    triplegService.getValidTripLegAllocationProperties(request).subscribe({
      next: (response) => {
        dispatch(setValidTripLegAllocationProperties(response));
        dispatch(setLoadingValidTripLegAllocationProperties(false));
        dispatch(setLoadedValidTripLegAllocationProperties(true));
      },
      error: (err) => {
        dispatch(setLoadingValidTripLegAllocationProperties(false));
        dispatch(setLoadedValidTripLegAllocationProperties(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const rejectTripLegAsync =
  (tripLegInfo: RejectTripLegRequest) => (dispatch: AppDispatch) => {
    dispatch(setSavingTripLeg(true));
    dispatch(setSavedTripLeg(false));
    reservationService.rejectTripLeg(tripLegInfo).subscribe({
      next: (tripLeg) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.tripLegRejectedSuccess'));
        dispatch(setSavingTripLeg(false));
        dispatch(setSavedTripLeg(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingTripLeg(false));
        dispatch(setSavedTripLeg(false));
      },
    });
  };

export const getTripLegDetailsAsync =
  (referenceID: string) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegDetails(true));
    triplegService.getTripLeg({ tripLegReferenceID: referenceID }).subscribe({
      next: (tripLeg) => {
        dispatch(addTripLegDetail(tripLeg));
        dispatch(setLoadingTripLegDetails(false));
      },
      error: (err) => {
        dispatch(setLoadingTripLegDetails(false));
      },
    });
  };

export const cancelRideshareTripLegAsync =
  (tripLegID: number) => (dispatch: AppDispatch) => {
    triplegService.cancelRideshareTripLeg({ tripLegID }).subscribe({
      next: () => {
        dispatch(uiSlice.setSuccessMessage('snackbar.tripLegCancelledSuccess'));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const fetchServiceConfigurationAsync = () => (dispatch: AppDispatch) => {
  dispatch(setLoadingServiceConfiguration(true));
  dispatch(setLoadedServiceConfiguration(false));
  triplegService.getServiceConfiguration().subscribe((config) => {
    dispatch(setServiceConfiguration(config));
    dispatch(setLoadingServiceConfiguration(false));
    dispatch(setLoadedServiceConfiguration(true));
  });
};

export const saveServiceConfigurationAsync =
  (config: InventoryServiceConfiguration) => (dispatch: AppDispatch) => {
    dispatch(setSavingServiceConfiguration(true));
    dispatch(setSavedServiceConfiguration(false));
    triplegService.saveServiceConfiguration(config).subscribe({
      next: (updatedConfig) => {
        dispatch(setServiceConfiguration(updatedConfig));
        dispatch(setSavingServiceConfiguration(false));
        dispatch(setSavedServiceConfiguration(true));
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.inventoryServiceConfigSaveSuccess',
          ),
        );
      },
      error: (err) => {
        dispatch(setSavingServiceConfiguration(false));
        dispatch(setSavedServiceConfiguration(true));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const fetchAllTripLegsInReservationAsync =
  (reservationReferenceID: string, passengerUUID: string) =>
  (dispatch: AppDispatch) => {
    dispatch(setLoadingAllTripLegsInReservation(true));
    dispatch(setLoadedAllTripLegsInReservation(false));
    reservationService
      .getReservationByReferenceID(reservationReferenceID, passengerUUID)
      .subscribe({
        next: (reservation) => {
          dispatch(setAllTripLegsInReservation(reservation.tripLegs));
          dispatch(setLoadingAllTripLegsInReservation(false));
          dispatch(setLoadedAllTripLegsInReservation(true));
        },
        error: (err) => {
          dispatch(setLoadingAllTripLegsInReservation(false));
          dispatch(setLoadedAllTripLegsInReservation(true));
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const setTripEscalationAsync =
  (tripLegReferenceID: string) => (dispatch: AppDispatch) => {
    triplegService.setTripEscalation({ tripLegReferenceID }).subscribe({
      next: () => {},
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const escalateTripLegAsync =
  (tripLegReferenceID: string) => (dispatch: AppDispatch) => {
    triplegService.setTripEscalation({ tripLegReferenceID }).subscribe({
      next: () => {
        dispatch(uiSlice.setSuccessMessage('snackbar.tripEscalationSuccess'));
        dispatch(setEscalatedTripLeg(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const clearTripEscalationAsync =
  (tripLegReferenceID: string) => (dispatch: AppDispatch) => {
    triplegService.clearTripEscalation({ tripLegReferenceID }).subscribe({
      next: () => {
        dispatch(uiSlice.setSuccessMessage('snackbar.tripEscalationSuccess'));
        dispatch(setEscalatedTripLeg(false));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const clearTripLegExceptionAsync =
  (tripLegReferenceID: string, notify?: boolean) => (dispatch: AppDispatch) => {
    dispatch(setSavingTripLegException(true));
    dispatch(setSavedTripLegException(false));
    triplegService.clearTripLegException({ tripLegReferenceID }).subscribe({
      next: () => {
        dispatch(setSavingTripLegException(false));
        dispatch(setSavedTripLegException(true));
        if (notify) {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.tripLegExceptionClearedSuccess',
            ),
          );
        }
      },
      error: (err) => {
        dispatch(setSavingTripLegException(false));
        dispatch(setSavedTripLegException(true));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const setTripLegExceptionAsync =
  (tripLegReferenceID: string, notify?: boolean) => (dispatch: AppDispatch) => {
    dispatch(setSavingTripLegException(true));
    dispatch(setSavedTripLegException(false));
    triplegService.setTripLegException({ tripLegReferenceID }).subscribe({
      next: () => {
        dispatch(setSavingTripLegException(false));
        dispatch(setSavedTripLegException(true));
        if (notify) {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.tripLegExceptionSuccess'),
          );
        }
      },
      error: (err) => {
        dispatch(setSavingTripLegException(false));
        dispatch(setSavedTripLegException(true));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const setTripLegExceptionAssignmentAsync =
  (tripLegReferenceID: string, userAccountID: number) =>
  (dispatch: AppDispatch) => {
    triplegService
      .setTripLegExceptionAssignment({ tripLegReferenceID, userAccountID })
      .subscribe({
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const fetchProvidersMatchingTripLegAsync =
  (tripLeg: TripLeg) => (dispatch: AppDispatch) => {
    dispatch(setLoadingProvidersMatchingTripLeg(true));
    dispatch(setLoadedProvidersMatchingTripLeg(false));
    triplegService.getProvidersMatchingTripLeg({ tripLeg }).subscribe({
      next: (providers) => {
        dispatch(setProvidersMatchingTripLeg(providers));
        dispatch(setLoadingProvidersMatchingTripLeg(false));
        dispatch(setLoadedProvidersMatchingTripLeg(true));
      },
    });
  };

export const getDiscussionAsync =
  (tripLegID: number) => (dispatch: AppDispatch) => {
    dispatch(setTripLegDiscussion(null));
    dispatch(setLoadingTripLegDiscussion(true));
    dispatch(setLoadedTripLegDiscussion(false));
    dispatch(setClearingDiscussion(false));
    dispatch(setClearedDiscussion(false));
    triplegService.getDiscussion({ tripLegID }).subscribe({
      next: (discussion) => {
        dispatch(setTripLegDiscussion(discussion));
        dispatch(setLoadedTripLegDiscussion(true));
      },
      error: () => {
        dispatch(setLoadedTripLegDiscussion(false));
      },
      complete: () => {
        dispatch(setLoadingTripLegDiscussion(false));
      },
    });
  };

export const clearUnderDiscussion =
  (
    tripLegID: number,
    decision: TripLegDiscussionDecision,
    tripLegExceptionID?: number,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setClearingDiscussion(true));
    dispatch(setClearedDiscussion(false));
    triplegService.clearUnderDiscussion({ tripLegID, decision }).subscribe({
      next: () => {
        if (tripLegExceptionID) {
          triplegService
            .setException({ tripLegID, tripLegExceptionID })
            .subscribe({
              next: () => {
                dispatch(setClearedDiscussion(true));
                dispatch(
                  uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
                );
                dispatch(removeDiscussionTripLeg(tripLegID));
              },
              error: () => {
                dispatch(setClearedDiscussion(false));
                dispatch(
                  uiSlice.setErrorMessage('snackbar.clearDiscussionFailure'),
                );
              },
              complete: () => {
                dispatch(setClearingDiscussion(false));
              },
            });
        } else {
          dispatch(setClearedDiscussion(true));
          dispatch(
            uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
          );
          dispatch(removeDiscussionTripLeg(tripLegID));
        }
      },
      error: () => {
        dispatch(setClearedDiscussion(false));
        dispatch(uiSlice.setErrorMessage('snackbar.clearDiscussionFailure'));
      },
      complete: () => {
        if (!tripLegExceptionID) {
          dispatch(setClearingDiscussion(false));
        }
      },
    });
  };

export type TripLegStatusUpdate = {
  passengerUUID: string;
  tripLegReferenceID: string;
  action: TripLegStatus;
  tripLegStatusReasonBCs?: string[];
  onSiteDateTimeUTC?: string;
  onBoardDateTimeUTC?: string;
  completedDateTimeUTC?: string;
  noLoadDateTimeUTC?: string;
  cancellationDateTimeUTC?: string;
};

export type DriverAssignment = {
  tripLegReferenceID: string;
  driverID: number;
};

export const submitAdjustTrip =
  (
    tripLegID: number,
    decision: TripLegDiscussionDecision,
    previousStatus?: string,
    tripLegStatusUpdate?: TripLegStatusUpdate,
    driverAssignment?: DriverAssignment,
    rejectTripLegInfo?: RejectTripLegRequest,
    tripLegExceptionID?: number,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setClearingDiscussion(true));
    dispatch(setClearedDiscussion(false));

    const handleError = (msg?: string) => {
      dispatch(setClearedDiscussion(false));
      dispatch(uiSlice.setErrorMessage('snackbar.clearDiscussionFailure', msg));
      dispatch(setClearingDiscussion(false));
    };

    const finishClearDiscussion = () => {
      triplegService.clearUnderDiscussion({ tripLegID, decision }).subscribe({
        next: () => {
          if (tripLegExceptionID) {
            triplegService
              .setException({ tripLegID, tripLegExceptionID })
              .subscribe({
                next: () => {
                  dispatch(setClearedDiscussion(true));
                  dispatch(
                    uiSlice.setSuccessMessage(
                      'snackbar.clearDiscussionSuccess',
                    ),
                  );
                  dispatch(removeDiscussionTripLeg(tripLegID));
                },
                error: (err) => handleError(err.message),
                complete: () => {
                  dispatch(setClearingDiscussion(false));
                },
              });
          } else {
            dispatch(setClearedDiscussion(true));
            dispatch(
              uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
            );
            dispatch(removeDiscussionTripLeg(tripLegID));
          }
        },
        error: (err) => {
          dispatch(setClearedDiscussion(false));
          dispatch(
            uiSlice.setErrorMessage(
              'snackbar.clearDiscussionFailure',
              err.message,
            ),
          );
        },
        complete: () => {
          if (!tripLegExceptionID) {
            dispatch(setClearingDiscussion(false));
          }
        },
      });
    };

    if (decision.discussionDecisionOutcome === 'NOTSERVICED') {
      const rejectPayload =
        rejectTripLegInfo ||
        (tripLegStatusUpdate && tripLegStatusUpdate.tripLegStatusReasonBCs
          ? {
              passengerUUID: tripLegStatusUpdate.passengerUUID,
              tripLegReferenceID: tripLegStatusUpdate.tripLegReferenceID,
              tripLegRejectedReasonBC:
                tripLegStatusUpdate.tripLegStatusReasonBCs[0],
            }
          : undefined);

      if (!rejectPayload) {
        handleError('Missing rejection details');
        return;
      }

      return reservationService.rejectTripLeg(rejectPayload).subscribe({
        next: () => {
          finishClearDiscussion();
        },
        error: (err) => {
          dispatch(setClearedDiscussion(false));
          dispatch(
            uiSlice.setErrorMessage(
              'snackbar.clearDiscussionFailure',
              err.message,
            ),
          );
          dispatch(setClearingDiscussion(false));
        },
      });
    }

    if (!tripLegStatusUpdate) {
      return finishClearDiscussion();
    }

    if (
      previousStatus === TripLegStatus.Completed &&
      tripLegStatusUpdate.action === TripLegStatus.NoLoad
    ) {
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.Onsite,
          tripLegStatusUpdate.onSiteDateTimeUTC!,
        )
        .subscribe({
          next: () => {
            return reservationService
              .updateTripLegLog(
                TripLegStatusBC.NoLoad,
                tripLegStatusUpdate.passengerUUID,
                tripLegStatusUpdate.tripLegReferenceID,
                tripLegStatusUpdate.noLoadDateTimeUTC!,
              )
              .subscribe({
                next: () => {
                  if (driverAssignment) {
                    triplegService
                      .assignDriver({
                        tripLegReferenceID: driverAssignment.tripLegReferenceID,
                        driverID: driverAssignment.driverID,
                        sendSMS: false,
                      })
                      .subscribe({
                        next: () => finishClearDiscussion(),
                        error: (err) => handleError(err.message),
                      });
                  } else {
                    finishClearDiscussion();
                  }
                },
                error: (err) => handleError(err.message),
              });
          },
          error: (err) => handleError(err.message),
        });
    }

    if (tripLegStatusUpdate.action === TripLegStatus.NoLoad) {
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.NoLoad,
          tripLegStatusUpdate.noLoadDateTimeUTC!,
        )
        .subscribe({
          next: () => {
            return reservationService
              .updateTripLegLog(
                TripLegStatusBC.Onsite,
                tripLegStatusUpdate.passengerUUID,
                tripLegStatusUpdate.tripLegReferenceID,
                tripLegStatusUpdate.onSiteDateTimeUTC!,
              )
              .subscribe({
                next: () => {
                  if (driverAssignment) {
                    triplegService
                      .assignDriver({
                        tripLegReferenceID: driverAssignment.tripLegReferenceID,
                        driverID: driverAssignment.driverID,
                        sendSMS: false,
                      })
                      .subscribe({
                        next: () => finishClearDiscussion(),
                        error: (err) => handleError(err.message),
                      });
                  } else {
                    finishClearDiscussion();
                  }
                },
                error: (err) => handleError(err.message),
              });
          },
          error: (err) => handleError(err.message),
        });
    }

    if (tripLegStatusUpdate.action === TripLegStatus.CanceledLate) {
      // Generic -> CanceledLate: override with reason code, then wait for the
      // TripLegCanceledLate WS event before calling clearUnderDiscussion.
      // The backend emits the event only after fully committing the status
      // change, so the driver-required validation in clearUnderDiscussion is
      // safe to call only after that event arrives.
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.CanceledLate,
          tripLegStatusUpdate.cancellationDateTimeUTC!,
          [TripLegStatusBC.LateDoctorReschedule],
        )
        .subscribe({
          next: () => {
            dispatch(
              setPendingDiscussionClear({
                tripLegID,
                tripLegReferenceID: tripLegStatusUpdate.tripLegReferenceID,
                decision,
                tripLegExceptionID,
              }),
            );
          },
          error: (err) => handleError(err.message),
        });
    }

    if (tripLegStatusUpdate.action === TripLegStatus.Canceled) {
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.Canceled,
          tripLegStatusUpdate.cancellationDateTimeUTC!,
          [TripLegStatusBC.LateDoctorReschedule],
        )
        .subscribe({
          next: () => {
            finishClearDiscussion();
          },
          error: (err) => handleError(err.message),
        });
    }

    if (tripLegStatusUpdate.action === TripLegStatus.Completed) {
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.Completed,
          tripLegStatusUpdate.completedDateTimeUTC!,
        )
        .subscribe({
          next: () => {
            return reservationService
              .updateTripLegLog(
                TripLegStatusBC.Onsite,
                tripLegStatusUpdate.passengerUUID,
                tripLegStatusUpdate.tripLegReferenceID,
                tripLegStatusUpdate.onSiteDateTimeUTC!,
              )
              .subscribe({
                next: () => {
                  reservationService
                    .updateTripLegLog(
                      TripLegStatusBC.Onboard,
                      tripLegStatusUpdate.passengerUUID,
                      tripLegStatusUpdate.tripLegReferenceID,
                      tripLegStatusUpdate.onBoardDateTimeUTC!,
                    )
                    .subscribe({
                      next: () => {
                        if (driverAssignment) {
                          triplegService
                            .assignDriver({
                              tripLegReferenceID:
                                driverAssignment.tripLegReferenceID,
                              driverID: driverAssignment.driverID,
                              sendSMS: false,
                            })
                            .subscribe({
                              next: () => finishClearDiscussion(),
                              error: (err) => handleError(err.message),
                            });
                        } else {
                          finishClearDiscussion();
                        }
                      },
                      error: (err) => handleError(err.message),
                    });
                },
                error: (err) => handleError(err.message),
              });
          },
          error: (err) => handleError(err.message),
        });
    }
    debugger;
    // TODO: there may be more scenarios to cover.
  };

export const completePendingDiscussionClearAsync =
  (tripLegReferenceID: string) =>
  (dispatch: AppDispatch, getState: () => any) => {
    const pending = getState().tripleg.pendingDiscussionClear;
    if (!pending || pending.tripLegReferenceID !== tripLegReferenceID) {
      return;
    }

    dispatch(setPendingDiscussionClear(null));

    const { tripLegID, decision, tripLegExceptionID } = pending;

    triplegService.clearUnderDiscussion({ tripLegID, decision }).subscribe({
      next: () => {
        if (tripLegExceptionID) {
          triplegService
            .setException({ tripLegID, tripLegExceptionID })
            .subscribe({
              next: () => {
                dispatch(setClearedDiscussion(true));
                dispatch(
                  uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
                );
                dispatch(removeDiscussionTripLeg(tripLegID));
              },
              error: () => {
                dispatch(setClearedDiscussion(false));
                dispatch(
                  uiSlice.setErrorMessage('snackbar.clearDiscussionFailure'),
                );
              },
              complete: () => {
                dispatch(setClearingDiscussion(false));
              },
            });
        } else {
          dispatch(setClearedDiscussion(true));
          dispatch(
            uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
          );
          dispatch(removeDiscussionTripLeg(tripLegID));
        }
      },
      error: () => {
        dispatch(setClearedDiscussion(false));
        dispatch(uiSlice.setErrorMessage('snackbar.clearDiscussionFailure'));
      },
      complete: () => {
        if (!tripLegExceptionID) {
          dispatch(setClearingDiscussion(false));
        }
      },
    });
  };

export const fetchReservationTripLegsAsync =
  (reservationReferenceID: string, tripLegIDs: string[]) =>
  (dispatch: AppDispatch, getState: () => any) => {
    dispatch(setLoadingReservationTripLegs(true));
    dispatch(setLoadedReservationTripLegs(false));
    const state = getState();
    const userInfo = state?.user?.loggedInUserInfo;
    const userCategoryTypeBC = userInfo?.userCategoryTypeBC;
    const clientBC = userInfo?.ids?.clientBC as string | undefined;
    triplegService
      .getTripLegsV2({
        page: 1,
        pageSize: 1000,
        sortModel: undefined,
        tripLegReferenceIDs: tripLegIDs,
        includeTripLegStatusLogs: true,
        clientBC: userCategoryTypeBC === 'CLIENT' ? clientBC : undefined,
      })
      .subscribe({
        next: (response) => {
          dispatch(
            setReservationTripLegs({
              reservationReferenceID,
              tripLegs: response,
            }),
          );
          dispatch(setLoadingReservationTripLegs(false));
          dispatch(setLoadedReservationTripLegs(true));
        },
        error: (err) => {
          dispatch(setLoadingReservationTripLegs(false));
        },
      });
  };

export const selectTripLegsSearchResults = (state: { tripleg: TriplegState }) =>
  state.tripleg.tripLegsSearchResults;
export const selectLoadingTripLegsSearch = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadingTripLegsSearch;
export const selectLoadedTripLegsSearch = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadedTripLegsSearch;

export const selectTripLegInventory = (state: { tripleg: TriplegState }) =>
  state.tripleg.tripLegInventory;
export const selectLoadingTripLegInventory = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingTripLegInventory;
export const selectLoadedTripLegInventory = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedTripLegInventory;
export const selectLoadedAllTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadedAllTripLegs;
export const selectLoadingAllTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadingAllTripLegs;
export const selectAllTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.allTripLegs;
export const selectAllTripLegsInReservation = (state: {
  tripleg: TriplegState;
}) => state.tripleg.allTripLegsInReservation;
export const selectLoadingAllTripLegsInReservation = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingAllTripLegsInReservation;
export const selectLoadedAllTripLegsInReservation = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedAllTripLegsInReservation;
export const selectTodaysTripsWithExceptions = (state: {
  tripleg: TriplegState;
}) => state.tripleg.todaysTripsWithExceptions;
export const selectLoadedTodaysTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadedTodaysTripLegs;
export const selectLoadingTodaysTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadingTodaysTripLegs;
export const selectTodaysTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.todaysTripLegs;
export const selectSavingTripLeg = (state: { tripleg: TriplegState }) =>
  state.tripleg.savingTripLeg;
export const selectSavedTripLeg = (state: { tripleg: TriplegState }) =>
  state.tripleg.savedTripLeg;
export const selectInventoryPage = (state: { tripleg: TriplegState }) =>
  state.tripleg.inventoryPage;
export const selectInventoryPageSize = (state: { tripleg: TriplegState }) =>
  state.tripleg.inventoryPageSize;
export const selectInventoryPageSortModel = (state: {
  tripleg: TriplegState;
}) => state.tripleg.inventoryPageSortModel;
export const selectMonitorPage = (state: { tripleg: TriplegState }) =>
  state.tripleg.monitorPage;
export const selectMonitorPageSize = (state: { tripleg: TriplegState }) =>
  state.tripleg.monitorPageSize;
export const selectMonitorPageSortModel = (state: { tripleg: TriplegState }) =>
  state.tripleg.monitorPageSortModel;
export const selectDiscussionPage = (state: { tripleg: TriplegState }) =>
  state.tripleg.discussionPage;
export const selectDiscussionPageSize = (state: { tripleg: TriplegState }) =>
  state.tripleg.discussionPageSize;
export const selectDiscussionPageSortModel = (state: {
  tripleg: TriplegState;
}) => state.tripleg.discussionPageSortModel;
export const selectDiscussionTripsLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.discussionTripLegs;
export const selectLoadedDiscussionTripLegs = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedDiscussionTripLegs;
export const selectLoadingDiscussionTripLegs = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingDiscussionTripLegs;
export const selectInvoicePage = (state: { tripleg: TriplegState }) =>
  state.tripleg.invoicePage;
export const selectInvoicePageSize = (state: { tripleg: TriplegState }) =>
  state.tripleg.invoicePageSize;
export const selectInvoicePageSortModel = (state: { tripleg: TriplegState }) =>
  state.tripleg.invoicePageSortModel;
export const selectInvoiceTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.invoiceTripLegs;
export const selectLoadedInvoiceTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadedInvoiceTripLegs;
export const selectLoadingInvoiceTripLegs = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingInvoiceTripLegs;
export const selectTodayPage = (state: { tripleg: TriplegState }) =>
  state.tripleg.todayPage;
export const selectTodayPageSize = (state: { tripleg: TriplegState }) =>
  state.tripleg.todayPageSize;
export const selectTodayPageSortModel = (state: { tripleg: TriplegState }) =>
  state.tripleg.todayPageSortModel;
export const selectTripLegDetails = (state: { tripleg: TriplegState }) =>
  state.tripleg.tripLegDetails;
export const selectLoadingTripLegDetails = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadingTripLegDetails;
export const selectServiceConfiguration = (state: { tripleg: TriplegState }) =>
  state.tripleg.serviceConfiguration;
export const selectLoadingServiceConfiguration = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingServiceConfiguration;
export const selectLoadedServiceConfiguration = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedServiceConfiguration;
export const selectSavingServiceConfiguration = (state: {
  tripleg: TriplegState;
}) => state.tripleg.savingServiceConfiguration;
export const selectSavedServiceConfiguration = (state: {
  tripleg: TriplegState;
}) => state.tripleg.savedServiceConfiguration;
export const selectEscalatedTripLeg = (state: { tripleg: TriplegState }) =>
  state.tripleg.escalatedTripLeg;
export const selectProvidersMatchingTripLeg = (state: {
  tripleg: TriplegState;
}) => state.tripleg.providersMatchingTripLeg;
export const selectLoadingProvidersMatchingTripLeg = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingProvidersMatchingTripLeg;
export const selectLoadedProvidersMatchingTripLeg = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedProvidersMatchingTripLeg;
export const selectTripLegEventEmitted = (state: { tripleg: TriplegState }) =>
  state.tripleg.tripLegEvent;
export const selectTripLegDiscussion = (state: { tripleg: TriplegState }) =>
  state.tripleg.tripLegDiscussion;
export const selectLoadingTripLegDiscussion = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingTripLegDiscussion;
export const selectLoadedTripLegDiscussion = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedTripLegDiscussion;
export const selectClearingDiscussion = (state: { tripleg: TriplegState }) =>
  state.tripleg.clearingDiscussion;
export const selectClearedDiscussion = (state: { tripleg: TriplegState }) =>
  state.tripleg.clearedDiscussion;
export const selectValidTripLegAllocationProperties = (state: {
  tripleg: TriplegState;
}) => state.tripleg.validTripLegAllocationProperties;
export const selectLoadingValidTripLegAllocationProperties = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingValidTripLegAllocationProperties;
export const selectLoadedValidTripLegAllocationProperties = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedValidTripLegAllocationProperties;
export const selectReservationTripLegs = (state: { tripleg: TriplegState }) =>
  state.tripleg.reservationTripLegs;
export const selectLoadingReservationTripLegs = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingReservationTripLegs;
export const selectLoadedReservationTripLegs = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedReservationTripLegs;
export const selectTripLegCounts = (state: { tripleg: TriplegState }) =>
  state.tripleg.tripLegCounts;
export const selectLoadingTripLegCounts = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadingTripLegCounts;
export const selectLoadedTripLegCounts = (state: { tripleg: TriplegState }) =>
  state.tripleg.loadedTripLegCounts;
export const selectMonitoringExceptionsTripLegs = (state: {
  tripleg: TriplegState;
}) => state.tripleg.monitoringExceptionsTripLegs;
export const selectLoadingMonitoringExceptionsTripLegs = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadingMonitoringExceptionsTripLegs;
export const selectLoadedMonitoringExceptionsTripLegs = (state: {
  tripleg: TriplegState;
}) => state.tripleg.loadedMonitoringExceptionsTripLegs;
export const selectSavingTripLegException = (state: {
  tripleg: TriplegState;
}) => state.tripleg.savingTripLegException;
export const selectSavedTripLegException = (state: { tripleg: TriplegState }) =>
  state.tripleg.savedTripLegException;

export default slice.reducer;
